import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { adminAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const formatDate = (value) => {
  if (!value) return 'Unpublished';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unpublished';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const MarkdownBlock = ({ content }) => {
  if (!content) return null;

  const lines = content.split('\n');
  return (
    <div className="space-y-2 text-sm leading-6 text-gray-700">
      {lines.map((line, index) => {
        const key = `${index}-${line}`;
        const trimmed = line.trim();

        if (!trimmed) {
          return <div key={key} className="h-1" />;
        }
        if (trimmed.startsWith('### ')) {
          return <h4 key={key} className="pt-2 text-sm font-semibold text-gray-900">{trimmed.slice(4)}</h4>;
        }
        if (trimmed.startsWith('## ')) {
          return <h3 key={key} className="pt-3 text-base font-semibold text-gray-900">{trimmed.slice(3)}</h3>;
        }
        if (trimmed.startsWith('# ')) {
          return <h2 key={key} className="pt-3 text-lg font-semibold text-gray-900">{trimmed.slice(2)}</h2>;
        }
        if (trimmed.startsWith('- ')) {
          return (
            <div key={key} className="flex gap-2">
              <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-blue-500" />
              <span>{trimmed.slice(2)}</span>
            </div>
          );
        }

        return <p key={key}>{trimmed}</p>;
      })}
    </div>
  );
};

const ChangeLog = ({ scope = 'customer' }) => {
  const [showAll, setShowAll] = useState(false);
  const isAdmin = scope === 'admin';
  const { data, isLoading } = useQuery(
    ['changelog', scope, showAll],
    () => adminAPI.getChangelog({ scope, all: showAll ? 'true' : undefined }),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  );

  const entries = data?.entries || [];
  const version = data?.version || process.env.REACT_APP_APP_VERSION || 'dev';

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-blue-600">Changelog</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-gray-900">
            What's new in version {version}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-600">
            Recent product updates from the last {data?.recentWindowDays || 90} days.
            {data?.fallbackToLatest ? ' No recent entries were found, so the latest published entry is shown.' : ''}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowAll((current) => !current)}
          className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
        >
          {showAll ? 'Show recent changes' : 'See all changelog entries'}
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="grid grid-cols-12 gap-4 border-b border-gray-200 bg-gray-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <div className="col-span-2">Version</div>
          <div className="col-span-2">Date</div>
          <div className={isAdmin ? 'col-span-5' : 'col-span-8'}>Update</div>
          {isAdmin && <div className="col-span-3">Audience</div>}
        </div>

        {entries.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-gray-500">
            No changelog entries have been published yet.
          </div>
        ) : entries.map((entry) => (
          <article key={entry.id} className="border-b border-gray-100 px-5 py-5 last:border-b-0">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 font-mono text-sm font-semibold text-gray-900 sm:col-span-2">
                {entry.version}
              </div>
              <div className="col-span-12 text-sm text-gray-500 sm:col-span-2">
                {formatDate(entry.published_at)}
              </div>
              <div className={isAdmin ? 'col-span-12 sm:col-span-5' : 'col-span-12 sm:col-span-8'}>
                <h2 className="text-base font-semibold text-gray-900">{entry.title}</h2>
                {entry.summary && <p className="mt-1 text-sm text-gray-600">{entry.summary}</p>}
                <div className="mt-4">
                  <MarkdownBlock content={entry.body_markdown} />
                </div>
              </div>
              {isAdmin && (
                <div className="col-span-12 sm:col-span-3">
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                    entry.admin_only
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-emerald-100 text-emerald-800'
                  }`}>
                    {entry.admin_only ? 'Admin only' : 'All users'}
                  </span>
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default ChangeLog;
